/**
 * This file has the message formats for the different messages that are
 * received by the client from the websocket.
 *
 * Each websocket `message` contains an `event` that has a:
 *  - `type`,
 *  - `parent_type` and
 *  - `data`.
 *
 * These `events` are then reused by the `store` and `actions`.
 */
import { BudgetMinMaxBackend } from '@freelancer/datastore/budget-min-max';
import { OnlineOfflineUserStatusValueApi } from '@freelancer/datastore/online-offline';
import { CurrencyApi, TimezoneApi } from 'api-typings/common/common';
import {
  ContextTypeApi,
  PrivacyLevelApi,
  RichMessageApi,
  SourceTypeApi,
  ThreadTypeApi,
} from 'api-typings/messages/messages_types';
import {
  BidAwardStatusApi,
  MilestoneReasonApi,
  MilestoneStatusApi,
} from 'api-typings/projects/projects';
import { TaskApi } from 'api-typings/tasklist/tasklist';
import { BalanceApi } from 'api-typings/users/users';
import { CurrencyAjax } from '../../currencies/currencies.model';
import { TimeSeconds, TimestampSeconds } from '../time/time.model';

export type WebsocketServerEvent = SubMessageEvent | WebsocketMessage;

interface SubMessageEvent {
  readonly channel: 'subscribe';
  readonly body: 'OK';
}

// --- Notification events (from event_notify.php and Python services) ---

// This models the data from getContestData() in event_notify.php
export type WebsocketContestData = TimeSeconds & {
  readonly id: string | number;
  readonly actionText: any;
  readonly appended_descr: string;
  readonly buyer: string;
  readonly buyerUrl: string;
  readonly imgUrl: string;
  readonly isTest: boolean;
  readonly jobs: ReadonlyArray<number> | ReadonlyArray<string>;
  readonly jobString: string;
  readonly linkUrl: string;
  readonly title: string;
  readonly userId: string;
  readonly userName: string;
};

// This models the data from pack_project() in event_notify.php
export interface WebsocketProjectData {
  readonly id: number;
  readonly actionText: string;
  readonly appended_descr: string;
  readonly imgUrl: string;
  readonly isTest: boolean;
  readonly jobs: ReadonlyArray<string> | null;
  readonly jobString: string;
  readonly linkUrl: string;
  readonly recruiter: boolean;
  readonly text: string;
  readonly title: string;
  readonly userId: string | number;
  readonly userName: string;
}

interface WebsocketServiceData {
  readonly id: number;
  readonly service_id: string | number;
  readonly service_name: string;
  readonly service_url: string;
  readonly service_duration: string;
  readonly service_cost: string | number;
  readonly currency_sign: string;
  readonly currency_code: string;
  readonly service_img: string; // A URL
}

type BaseServerData = TimestampSeconds & {
  readonly id: string;
};

export interface WebsocketArticleCommentEvent extends BaseServerData {
  readonly type: 'articleCommentReceived';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly article_author: string | number; // The ID of author
    readonly article_name: string;
    readonly article_type: string; // Some enum?
    readonly comment_by: string | number;
    readonly comment_date:
      | string
      | {
          readonly timezone_type: number;
          readonly date: string;
          readonly timezone: string;
        };
    readonly comment_id: string | number;
    readonly comment: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly publicName: string;
    readonly user: string;
  };
}

export interface WebsocketAwardEvent extends BaseServerData {
  readonly type: 'award';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly acceptByTime?: number;
    readonly actionText?: string;
    readonly apiMessage: {
      readonly bid: {
        readonly amount: number;
        readonly award_status: BidAwardStatusApi.PENDING;
        readonly bidder_id: number;
        readonly id: number;
        readonly period: number;
      };
      readonly project: {
        readonly currency: { readonly code: string; readonly sign: string };
        readonly id: number;
        readonly owner_id: number;
        readonly title: string;
        readonly type: 'fixed' | 'hourly';
      };
      readonly users: { readonly [id: string]: { readonly username: string } };
    };
    readonly appended_descr?: string;
    readonly awardeeId?: number;
    readonly awardeeName?: string;
    readonly bid?: {
      readonly bidAmount: string;
      readonly bidDescr: string | null;
      readonly bidId: number;
      readonly bidPeriod: string;
      readonly id: number;
    };
    readonly categoryName?: string;
    readonly currencycode?: string;
    readonly currencysign?: string;
    readonly id: number;
    readonly imgUrl?: string;
    readonly isTest?: boolean;
    readonly jobs: ReadonlyArray<string>;
    readonly jobString: string;
    readonly linkUrl?: string;
    readonly projIsHourly?: boolean;
    readonly text?: string;
    readonly title: string;
    readonly userId: number;
    readonly userName: string;
  };
}

export interface WebsocketAwardBadgeEvent extends BaseServerData {
  readonly type: 'awardBadge';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly descr: string;
    readonly id: number;
    readonly linkUrl: string;
    readonly name: string;
  };
}

export interface WebsocketAwardCreditEvent extends BaseServerData {
  readonly type: 'awardCredit';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly amount: string | number;
    readonly id: string | number;
    readonly linkUrl: string;
  };
}

export interface WebsocketAwardReminderEvent extends BaseServerData {
  readonly type: 'awardReminder';
  readonly parent_type: 'notifications';
  readonly data: {
    // Note, this doesn't actually have an `id`.
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly userName: string;
    readonly bid: { readonly id: string | number };
    // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
  };
}

export interface WebsocketAwardMilestoneReminderEvent extends BaseServerData {
  readonly type: 'awardMilestoneReminder';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly appended_descr: string;
    readonly currencycode: string;
    readonly currencyid: string | number;
    readonly currencysign: string;
    readonly id: string | number;
    readonly imgUrl: string;
    // isAutomaticPayments: boolean;
    readonly linkUrl: string;
    // projIsHourly: boolean;
    readonly publicName?: string;
    readonly title: string;
    readonly userId: string | number;
    readonly userName: string;
    // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
  };
}

export interface WebsocketAwardXpEvent extends BaseServerData {
  readonly type: 'awardXp';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly amount: string | number;
    readonly descr: string;
    readonly game: 'xp';
    readonly id: string | number;
    readonly linkUrl: string;
  };
}

export interface WebsocketBalanceUpdateEvent extends BaseServerData {
  readonly type: 'balanceUpdate';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly balances: ReadonlyArray<BalanceApi>;
    readonly primary_currency: CurrencyApi;
  };
}

interface WebsocketBidEventProjectCount {
  readonly all: number;
  readonly incomplete_reviews: number;
  readonly complete: number;
  readonly completion_rate: number;
  readonly reviews: number;
  readonly earnings: number;
  readonly incomplete: number;
}

interface WebsocketBidEventReputation {
  readonly com: number;
  readonly exp: number;
  readonly hag: number;
  readonly onb: number;
  readonly ont: number;
  readonly overall: number;
  readonly pos: number;
  readonly pro: number;
  readonly qua: number;
}

interface WebsocketBidEventSellerRating {
  readonly count: number;
  readonly projectCount: {
    readonly last12months: WebsocketBidEventProjectCount;
    readonly last3months: WebsocketBidEventProjectCount;
    readonly entire_history: WebsocketBidEventProjectCount;
  };
  readonly completionRate: number;
  readonly avg: number;
  readonly reputation: {
    readonly last12months: WebsocketBidEventReputation;
    readonly last3months: WebsocketBidEventReputation;
    readonly entire_history: WebsocketBidEventReputation;
  };
}

export interface WebsocketBidretractedEvent extends BaseServerData {
  readonly type: 'bidretracted';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly bid_id: number;
    readonly project_id: number;
    readonly seller_id: number;
  };
}

export interface WebsocketBidEvent extends BaseServerData {
  readonly type: 'bid' | 'bid_updated' | 'bid_self';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly accountId?: number;
    readonly amount: string;
    readonly award?: string;
    readonly bidAvg?: string | number | false;
    readonly bidCount?: number;
    readonly bidHidden: boolean;
    readonly bidNo?: string | false;
    readonly buyerFeePerDollar?: string;
    readonly buyerMinProjectFee?: string;
    readonly categoryName?: string;
    readonly currency: string;
    readonly currencyCode: string;
    readonly currencyId: string;
    readonly descr: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly initMilestone?: number;
    readonly linkUrl?: string;
    readonly period: string;
    readonly pmb: any;
    readonly projectId: string | number;
    readonly projIsFullTime: boolean;
    readonly projIsHourly: boolean;
    readonly projName: string;
    readonly publicName?: string;
    readonly showAwardFee: boolean;
    readonly text?: string;
    readonly title: string;
    readonly userId: string | number;
    readonly userName: string;
    readonly userBalances?: {
      readonly all: ReadonlyArray<{
        readonly currency: {
          readonly code: string;
          readonly country: string;
          readonly exchangerate: string;
          readonly id: string;
          readonly is_external?: string;
          readonly name: string;
          readonly seq: string;
          readonly sign: string;
        };
        readonly balance: string;
      }> | null;
      readonly relevant: number;
    } | null;
    readonly bid: {
      readonly award_score?: number;
      readonly completed?: boolean;
      readonly completion_score?: number;
      readonly descr_html?: string;
      readonly descr_short_html?: string;
      readonly descr_short?: string;
      readonly descr?: string | null;
      // expert_guarantee?: ExpertGuaranteeApi | null; // These fields in ExpertGuaranteeApi can be strings in the actualy websocket
      readonly highlighted?: boolean | null;
      readonly id: string | number;
      readonly isShortlisted?: boolean | null;
      readonly milestone_percentage?: string | number;
      readonly new_ranking?: number | boolean;
      readonly no?: string | false;
      readonly paid_status?: false | null;
      readonly period?: string;
      readonly project_id?: string | number;
      readonly r2?: number;
      readonly ranking?: number;
      readonly score?: number;
      readonly sponsored?: boolean | null;
      readonly submitdate_f?: string;
      readonly submitdate_f2?: string;
      readonly submitdate_ts?: number;
      readonly submitdate?: string;
      readonly sum?: string;
      readonly users_id?: number;
      readonly user?: {
        readonly address: {
          readonly country: string | null;
          readonly code: string;
          readonly city: string | null;
        };
        readonly company: string | null;
        readonly currency: number;
        readonly earnings: string | number;
        readonly flag_class: string;
        readonly flag_name: string | null;
        readonly flag: { readonly name: string | null; readonly icon: string };
        readonly gold: number;
        readonly hourlyrate: number | null;
        readonly id: number;
        readonly insignia: ReadonlyArray<{
          readonly name: string;
          readonly title: string;
          readonly examEnabled: string | null;
          readonly examId: string | null;
          readonly id: string;
          readonly description: string;
        }>;
        readonly isPortfolioCreated: boolean;
        readonly jobs: ReadonlyArray<string>;
        readonly logo_Url?: string;
        readonly normalMemberclassLogo: string;
        readonly online?: string | null;
        readonly profile_logo_Url?: string;
        readonly provider_rating: {
          readonly count: number;
          readonly avg: string | number;
        };
        readonly publicName?: string;
        readonly rating: {
          readonly count: number;
          readonly avg: string | number;
        };
        readonly reg_date: string;
        readonly reputation_rate: number;
        readonly timezone_info?: TimezoneApi; // This will be missing in old newsfeed objects.
        readonly tooltipMemberclassLogo: string;
        readonly Url?: string;
        readonly username: string;
        readonly seller_rating: WebsocketBidEventSellerRating;
      };
    };
  };
}

interface BidWebsocketApiMessage {
  readonly bid: {
    readonly amount?: number;
    readonly award_status: BidAwardStatusApi;
    readonly bidder_id: number;
    readonly id: number;
    readonly period: number;
  };
  readonly project: {
    readonly currency?: {
      readonly code: string;
      readonly id: number;
      readonly sign: string;
    };
    readonly id: number;
    readonly owner_id: number;
    readonly title?: string;
  };
  readonly users?: {
    readonly [index: string]: { readonly username: string };
  };
}

export interface WebsocketAcceptedEvent {
  readonly parent_type: 'notifications';
  readonly type: 'accepted';
  readonly data: {
    readonly bid: {
      readonly retracted: boolean;
      readonly descr: string | null;
    };
    readonly apiMessage: BidWebsocketApiMessage;
  };
}

export interface WebsocketRevokedEvent {
  readonly parent_type: 'notifications';
  readonly type: 'revoked';
  readonly data: {
    readonly apiMessage: BidWebsocketApiMessage;
    readonly awardeeId: number;
    readonly bidId: string | number;
    readonly projectId: number;
  };
}

export interface WebsocketCancelMilestoneEvent {
  readonly type: 'cancelMilestone';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly id: number;
    readonly tranId: string | number;
  };
}

export interface WebsocketCompletedEvent extends BaseServerData {
  readonly type: 'completed';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly amount: string | number;
    readonly categoryName: string;
    readonly completeStatus: string; // TODO: T37445 Make this a string enum
    readonly currencycode: string;
    readonly currencyid: string | number;
    readonly currencysign: string;
    readonly employerId: string | number;
    readonly employerPublicName?: string;
    readonly employerUsername: string;
    readonly expiredTimestamp: string;
    readonly freelancerId: string | number;
    readonly freelancerPublicName?: string;
    readonly freelancerUsername: string;
    readonly id: string | number;
    readonly img_e: { readonly profile_logo_url: string }; // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
    readonly img_f: { readonly profile_logo_url: string }; // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
    readonly name: string;
    // projIsHourly: boolean;
    readonly selectionId: string | number;
    readonly seoUrl: string;
    readonly state?: string; // TODO: T37445 Make this a string enum
    readonly submitDate: string;
  };
}

export interface WebsocketContestEvent extends BaseServerData {
  readonly type: 'contest';
  readonly parent_type: 'notifications';
  readonly data: WebsocketContestData & {
    readonly currency?: string;
    readonly currencyCode: string;
    readonly exchangerate: string;
    readonly extended: boolean;
    readonly featured: boolean;
    readonly free_bid_until?: number;
    readonly highlight: any;
    readonly maxbudget?: string | number;
    readonly private: boolean;
    readonly sealed: boolean;
    readonly submitDate: string;
  };
}

export interface WebsocketContestAwardedToEmployerEvent extends BaseServerData {
  readonly type: 'contestAwardedToEmployer';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly contestName: string;
    readonly contestSEOUrl?: string;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly entry_id: string;
    readonly entry_number: string;
    readonly entryCount: string;
    readonly freelancerCount: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly isBought: boolean;
    readonly linkUrl: string;
    readonly prize: string | number;
    readonly publicName?: string;
    readonly thumb: string;
    readonly userId: number;
    readonly userName: string;
  };
}

export interface WebsocketContestAwardedToFreelancerEvent
  extends BaseServerData {
  readonly type: 'contestAwardedToFreelancer';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly contestName: string;
    readonly contestSEOURL: string;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly entry_id: string | number;
    readonly entry_number: string | number;
    readonly entryCount: string | number;
    readonly freelancerCount: string | number;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly prize: string | number;
    readonly publicName?: string;
    readonly userId: string | number;
    readonly userName: string;
    // thumb: ???;
  };
}

export interface WebsocketContestEntryBoughtToEmployerEvent
  extends BaseServerData {
  readonly type: 'contestEntryBoughtToEmployer';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly contestName: string;
    readonly entry_id: string | number;
    readonly entry_number: string | number;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly publicName?: string;
    readonly userName: string;
  };
}

export interface WebsocketContestEntryBoughtToFreelancerEvent
  extends BaseServerData {
  readonly type: 'contestEntryBoughtToFreelancer';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly contestName: string;
    readonly entry_id: string | number;
    readonly entry_number: string | number;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly publicName?: string;
    readonly userName: string;
  };
}

export interface WebsocketContestEntryEvent extends BaseServerData {
  readonly type: 'contestEntry';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly contestHolder: string;
    readonly contestId: string;
    readonly contestName: string;
    readonly firstUser?: string;
    readonly firstUserPublicName?: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly secondUser?: string | null;
    readonly secondUserPublicName?: string | null;
    readonly userId: string | number;
  };
}

export interface WebsocketContestEntryRatedToFreelancerEvent
  extends BaseServerData {
  readonly type: 'contestEntryRatedToFreelancer';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly action: string;
    readonly contestId: string;
    readonly contestName: string;
    readonly employerId: string;
    readonly entriesUrl: string;
    readonly entryId: string;
    readonly entryNumber: string;
    readonly entryS3path: string;
    readonly entryThumb: string;
    readonly entryUrl: string;
    readonly freelancerId: string;
    readonly hasOtherEntriesRatedOrRejected: boolean;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly ratedOrRejectedEntriesCount: number;
    readonly rating: string;
  };
}

export interface WebsocketContestEntryRejectedToFreelancerEvent
  extends BaseServerData {
  readonly type: 'contestEntryRejectedToFreelancer';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly action: string;
    readonly contestId: string;
    readonly contestName: string;
    readonly employerId: string;
    readonly entriesUrl: string;
    readonly entryId: string;
    readonly entryNumber: string;
    readonly entryS3path: string;
    readonly entryThumb: string;
    readonly entryUrl: string;
    readonly freelancerId: string;
    readonly hasOtherEntriesRatedOrRejected: boolean;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly ratedOrRejectedEntriesCount: number;
    readonly rating: string;
  };
}

export interface WebsocketContestEntryReconsideredToFreelancerEvent
  extends BaseServerData {
  readonly type: 'contestEntryReconsideredToFreelancer';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly action: string;
    readonly contestId: string;
    readonly contestName: string;
    readonly employerId: string;
    readonly entriesUrl: string;
    readonly entryId: string;
    readonly entryNumber: string;
    readonly entryS3path: string;
    readonly entryThumb: string;
    readonly entryUrl: string;
    readonly freelancerId: string;
    readonly hasOtherEntriesRatedOrRejected: boolean;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly ratedOrRejectedEntriesCount: number;
    readonly rating: string;
  };
}

export interface WebsocketContestEntryCommentedToFreelancerEvent
  extends BaseServerData {
  readonly type: 'contestEntryCommentedToFreelancer';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly contestId: string;
    readonly contestName: string;
    readonly entryNumber: string;
    readonly id: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export interface WebsocketContestExpiredEvent extends BaseServerData {
  readonly type: 'contestExpired';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly contest_id: string | number;
    // ???
  };
}

export interface WebsocketContestPCBNotificationEvent extends BaseServerData {
  readonly type: 'contestPCBNotification';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly contestHolder: string;
    readonly contestId: string;
    readonly contestName: string;
    readonly firstUser?: string;
    readonly firstUserPublicName?: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly secondUser?: string | null;
    readonly secondUserPublicName?: string | null;
    readonly userId: number;
    readonly userCount: number;
  };
}

export interface WebsocketContestPCBNotificationFullViewEvent
  extends BaseServerData {
  readonly type: 'contestPCBNotificationFullView';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly contestHolder: string | number;
    readonly contestName: string;
    readonly entryNumber: string | number | null;
    readonly firstUser: string;
    readonly firstUserPublicName?: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly secondUser?: string | null;
    readonly secondUserPublicName?: string | null;
    readonly userId: string | number;
    readonly userCount: number;
  };
}

export interface WebsocketMilestone {
  readonly transaction_id?: number;
  readonly project_owner_id?: string;
  readonly bidder_id?: string;
  readonly amount?: number;
  readonly reason?: MilestoneReasonApi | 'partly_payment';
  readonly other_reason?: string;
  readonly project_id?: string | number;
  readonly bid_id?: number;
  readonly currency?: CurrencyAjax;
  readonly is_from_prepaid?: boolean;
  readonly status?: MilestoneStatusApi;
  readonly dispute_id: string | null;
  readonly time_created?: string;
}

export interface WebsocketCreateMilestoneEvent extends BaseServerData {
  readonly type: 'createMilestone';
  readonly parent_type: 'notifications';
  readonly data: WebsocketProjectData & {
    readonly account_id: string | number | null;
    readonly amount: string | number;
    readonly bidId: string | number;
    readonly currencycode: string;
    readonly currencysign: string;
    readonly project_id: string | number;
    readonly publicName?: string;
    readonly shortDescr: string;
    readonly transId: string | number;
    /* From NewEscrowHandler::save */
    readonly newMilestone: WebsocketMilestone;
  };
}

export interface WebsocketDenyedEvent extends BaseServerData {
  readonly type: 'denyed';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly apiMessage: {
      readonly project: {
        readonly id: number;
        readonly title: string;
        readonly owner_id: number;
        readonly currency: {
          readonly sign: string;
          readonly code: string;
        };
      };
      readonly bid: {
        readonly bidder_id: number;
        readonly award_status: 'rejected';
      };
      readonly users: {
        readonly [userId: string]: { readonly username: string };
      };
    };
    readonly autoRepostProjectId?: string | number;
    readonly autoRepostProjectName?: string;
    readonly bidId: string | number;
    readonly currencycode: string;
    readonly currencyId: string | number;
    readonly currencysign: string;
    readonly id: string | number;
    // img: ???;
    readonly imgUrl: string;
    readonly jobsURL: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly projectType: string; // TODO: T37445 Make this a string enum
    readonly projIsHourly: any;
    readonly publicName?: string;
    readonly reason: string;
    readonly seoUrl: string;
    readonly userId: string | number;
    readonly userName: string;
  };
}

export interface WebsocketDraftContestEvent extends BaseServerData {
  readonly type: 'draftContest';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly contestEditUrl?: string;
    readonly contestId: string;
    readonly contestName: string;
    readonly contestUrl: string;
    readonly imgUrl: string;
    readonly isPublished: boolean;
  };
}

export interface WebsocketFailingProjectEvent extends BaseServerData {
  readonly type: 'failingProject';
  readonly parent_type: 'notifications';
  readonly data: WebsocketProjectData &
    BudgetMinMaxBackend & {
      readonly address?: string;
      readonly currency?: string;
      readonly currencyCode: string;
      readonly featured: boolean;
      readonly free_bid_until?: number;
      readonly fulltime: boolean;
      readonly hideBids: boolean;
      readonly nonpublic: boolean;
      readonly projectFreeBid: boolean;
      readonly projIsHourly: boolean;
      readonly publicName?: string;
      readonly submitDate: string;
      readonly xpValue: number;
    };
}

export interface WebsocketGiveGetChildReceivedSignupBonusEvent
  extends BaseServerData {
  readonly type: 'giveGetChildReceivedSignupBonus';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly parentId: number;
    readonly childId: number;
    readonly bonus: number;
    readonly currencyCode: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export interface WebsocketGiveGetChildSignUpEvent extends BaseServerData {
  readonly type: 'giveGetChildSignUp';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly parentId: number;
    readonly childId: number;
    readonly childName: string;
    readonly bonus: number;
    readonly bonusRequirement: number;
    readonly currencyCode: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export interface WebsocketGiveGetChildPartialMilestoneRelease
  extends BaseServerData {
  readonly type: 'giveGetChildPartialMilestoneRelease';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly parentId: number;
    readonly childId: number;
    readonly childName: string;
    readonly bonusRequirement: number;
    readonly currencyCode: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export interface WebsocketGiveGetParentBonusEvent extends BaseServerData {
  readonly type: 'giveGetParentBonus';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly parentId: number;
    readonly bonus: number;
    readonly currencyCode: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export interface WebsocketHighLtvAnnualDiscountEvent extends BaseServerData {
  readonly type: 'highLtvAnnualDiscount';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly id: string | number;
    readonly userDisplayName: string;
    readonly linkUrl: string;
  };
}

export interface WebsocketHireMeEvent extends BaseServerData {
  readonly type: 'hireMe';
  readonly parent_type: 'notifications';
  // NOTE: doesn't have isTest, so can't use WebsocketProjectData
  readonly data: {
    // acceptByTime: $acceptByTime;
    // accountId: $project->getAccountId();
    readonly appended_descr: string;
    // bid: $myData['bid'];
    readonly currency?: { readonly code: string; readonly sign: string };
    // currencyCode: string;
    // currencyId: string | number;
    // currencySign: string;
    // featured: $project->isFeatured();
    readonly id: string | number;
    // img: $img;
    readonly imgUrl: string;
    readonly jobString: string;
    readonly linkUrl: string;
    // name: string;
    readonly period?: string | number;
    readonly projIsHourly: boolean;
    readonly publicName?: string;
    // seoUrl: $project->getSeoUrl();
    readonly sum?: string | number;
    readonly title: string;
    readonly userId: number;
    readonly userName: string;
  };
}

export interface WebsocketInviteUserBidEvent extends BaseServerData {
  readonly type: 'inviteUserBid';
  readonly parent_type: 'notifications';
  readonly data: WebsocketProjectData & {
    readonly currencycode: string;
    readonly currencysign: string;
    readonly inviteeId: string | number;
    readonly minBudget?: number; // The only place with camelCase min/maxBudget
    readonly maxBudget?: number | false; // TODO: use BudgetMinMaxBackend if possible (requires backend changes)
    readonly inviteeName: string;
    // projIsHourly: boolean;
    readonly publicName?: string;
  };
}

export interface WebsocketInviteToContestEvent extends BaseServerData {
  readonly type: 'inviteToContest';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly buyer: string | number; // The ID
    readonly buyerName: string;
    readonly buyerPublicName?: string;
    readonly buyerUrl: string;
    readonly contest_recommended_skills: string;
    readonly currencycode: string;
    readonly currencysign: string;
    readonly description: string;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly prize: string | number;
    readonly sellerId: string | number;
    readonly sellerName: string;
    readonly sellerPublicName?: string;
    readonly title: string;
    readonly type: string | null;
  };
}

export interface WebsocketInvoiceRequestedEvent extends BaseServerData {
  readonly type: 'invoiceRequested';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly accountId: string | number;
    readonly amount: string | number;
    readonly bidId: string | number;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly dueDate: string;
    readonly id: string | number;
    // img: ???;
    readonly imgUrl: string;
    // invoiceVars: ???;
    readonly invoiceId: string | number;
    readonly linkUrl: string;
    readonly name: string;
    // projIsHourly: boolean;
    readonly publicName?: string;
    readonly sellerId: string | number;
    readonly seoUrl: string;
    readonly state?: string; // TODO: T37445 Make this a string enum
    readonly userId: string | number;
    readonly userName: string;
  };
}

export interface WebsocketLevelUpEvent extends BaseServerData {
  readonly type: 'levelUp';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly id: string | number;
    readonly level: string;
    readonly membership: boolean;
    readonly perks: ReadonlyArray<{
      readonly description: string;
      readonly item: boolean;
      readonly magnitude: number;
      readonly name?: 'BidRefreshRate' | 'item';
      readonly symbol: string;
    }>;
    readonly username: string;
    readonly linkUrl: string;
    readonly game: 'level';
  };
}

export interface WebsocketLocalJobPostedEvent extends BaseServerData {
  // Note: this is actually an extension of WebsocketProjectEvent's data.
  readonly type: 'localJobPosted';
  readonly parent_type: 'notifications';
  readonly data: WebsocketProjectData &
    BudgetMinMaxBackend & {
      readonly address?: string | null;
      readonly currency?: string;
      readonly currencyCode: string;
      readonly exchangerate: string;
      readonly featured: boolean;
      readonly free_bid_until?: number;
      readonly fulltime: boolean;
      readonly hideBids: boolean;
      readonly isLocal: true;
      readonly nonpublic: boolean;
      readonly projIsHourly: boolean;
      readonly submitDate: string;
      readonly publicName?: string;
    };
}

// event_notify.php#logout returns an empty object, so no additional fields
// in this one
export interface WebsocketLogoutEvent extends BaseServerData {
  readonly type: 'logout';
  readonly parent_type: 'notifications';
  readonly data: {};
}

export interface WebsocketNotifyFollowerEvent extends BaseServerData {
  readonly type: 'notifyfollower';
  readonly parent_type: 'notifications';
  readonly data: WebsocketProjectData;
}

export interface WebsocketProjectEvent extends BaseServerData {
  readonly type: 'project';
  readonly parent_type: 'notifications';
  readonly data: WebsocketProjectData &
    BudgetMinMaxBackend & {
      readonly address?: string;
      readonly currency?: string;
      readonly currencyCode: string;
      readonly exchangerate: string;
      readonly featured: boolean;
      readonly free_bid_until?: number;
      readonly fulltime: boolean;
      readonly hideBids: boolean;
      readonly isLocal: boolean;
      readonly nonpublic: boolean;
      readonly projIsHourly: boolean;
      readonly submitDate: string | false;
      readonly publicName?: string;
    };
}

export interface WebsocketRecruiterProjectEvent extends BaseServerData {
  readonly type: 'recruiterProject';
  readonly parent_type: 'notifications';
  readonly data: WebsocketProjectData &
    BudgetMinMaxBackend & {
      readonly address?: string;
      readonly currency?: string;
      readonly currencyCode: string;
      readonly exchangerate: string;
      readonly featured: boolean;
      readonly free_bid_until?: number;
      readonly fulltime: boolean;
      readonly hideBids: boolean;
      readonly isLocal: boolean;
      readonly nonpublic: boolean;
      readonly projIsHourly: boolean;
      readonly submitDate: string;
      readonly publicName?: string;
    };
}

export interface WebsocketQuickHireProjectEvent extends BaseServerData {
  readonly type: 'quickHireProject';
  readonly parent_type: 'notifications';
  readonly data: WebsocketProjectData & {
    readonly bidderId: string | number;
    readonly bidderName: string;
    readonly currency?: { readonly code: string; readonly sign: string };
    // featured: boolean; // TODO: T37447 Are these boolean or something different?
    // fulltime: boolean; // TODO: T37447 Are these boolean or something different?
    // hideBids: boolean; // TODO: T37447 Are these boolean or something different?
    // nonpublic: boolean; // TODO: T37447 Are these boolean or something different?
    readonly period?: string | number;
    readonly projIsHourly: boolean;
    readonly publicName?: string;
    readonly submitDate: string;
    readonly sum?: string | number;
  };
}

export interface WebsocketReleaseMilestoneEvent extends BaseServerData {
  readonly type: 'releaseMilestone';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly accountId: string | number;
    readonly amount: string | number;
    readonly currencycode: string;
    readonly currencysign: string;
    readonly id: string | number;
    // img: ???;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly otherReason: string;
    readonly publicName?: string;
    readonly seoUrl: string;
    readonly submitDate: string;
    readonly title: string;
    readonly userId: string | number;
    readonly userName: string;
  };
}

export interface WebsocketRequestEndProjectEvent extends BaseServerData {
  readonly type: 'requestEndProject';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly accountId: string | number;
    readonly bidId: string | number;
    readonly categoryName: string;
    readonly dayCount: string | number;
    readonly id: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName?: string;
    readonly seoUrl: string;
    readonly submitDate: string;
    readonly userId: string | number;
    readonly userName: string;
  };
}

export interface WebsocketRequestMilestoneEvent extends BaseServerData {
  readonly type: 'requestMilestone';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly accountId: string | number;
    readonly amount: string | number;
    readonly bidId: string | number;
    readonly currencyCode: string;
    readonly currencyId: string | number;
    readonly currencySign: string;
    readonly description: string;
    readonly id: string | number;
    // img: ???;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName?: string;
    readonly requestId: string | number;
    readonly sellerId: string | number;
    readonly seoUrl: string;
    readonly state?: string; // TODO: T37445 Make this a string enum
    readonly submitDate: string;
    readonly userId: string | number;
    readonly userName: string;
  };
}

export interface WebsocketRequestToReleaseEvent extends BaseServerData {
  readonly type: 'requestToRelease';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly accountId: string | number;
    readonly amount: string | number;
    readonly bidId: string | number;
    readonly currencycode: string;
    readonly currencysign: string;
    readonly descr: string;
    readonly id: string | number;
    // img: ???;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName?: string;
    readonly seoUrl: string;
    readonly state?: string; // TODO: T37445 Make this a string enum
    readonly tranId: string | number;
    readonly userId: string | number;
    readonly userName: string;
  };
}

export interface WebsocketServiceApprovedEvent extends BaseServerData {
  readonly type: 'serviceApproved';
  readonly parent_type: 'notifications';
  readonly data: WebsocketServiceData;
}

export interface WebsocketServiceProjectEndingSellerEvent
  extends BaseServerData {
  readonly type: 'serviceProjectEndingSeller';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly bid_id: string | number;
    readonly buyer_id: string | number;
    readonly buyer_name: string;
    readonly hours_remaining: number;
    readonly id: string | number;
    readonly order_start_date: string;
    readonly project_id: string | number;
    readonly project_name: string;
    readonly project_url: string;
    readonly seller_id: string | number;
    readonly seller_name: string;
    readonly service_id: string | number;
    readonly service_name: string;
  };
}

export interface WebsocketServiceProjectPostedSellerEvent
  extends BaseServerData {
  readonly type: 'serviceProjectPostedSeller';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly bid_id: string | number;
    readonly buyer_id: string | number;
    readonly buyer_img_url: string;
    readonly buyer_name: string;
    readonly buyer_public_name?: string;
    readonly currency_code: string;
    readonly currency_sign: string;
    readonly id: string | number;
    readonly order_end_date: string;
    readonly order_start_date: string;
    readonly project_cost: string | number;
    // project_duration: ???;
    readonly project_id: string | number;
    // project_milestones: ???;
    readonly project_name: string;
    readonly project_url: string;
    readonly seller_id: string | number;
    readonly seller_img_url: string;
    readonly seller_name: string;
    readonly seller_public_name: string;
    readonly service_id: string | number;
    readonly service_name: string;
    readonly service_url: string;
  };
}

export interface WebsocketServiceStatusChangedEvent extends BaseServerData {
  readonly type: 'serviceStatusChanged';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly id: string | number;
    // ???
  };
}

export interface WebsocketServiceRejectedEvent extends BaseServerData {
  readonly type: 'serviceRejected';
  readonly parent_type: 'notifications';
  readonly data: WebsocketServiceData;
}

export interface WebsocketSignUpFreeTrialUpsellEvent extends BaseServerData {
  readonly type: 'signUpFreeTrialUpsell';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly id: string | number;
    readonly linkUrl: string;
    readonly trialPackageName: string;
  };
}

export interface WebsocketUpsellCrowdsourceFindingEvent extends BaseServerData {
  readonly type: 'upsellCrowdsourceFinding';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly id: string | number;
    readonly project_name: string;
  };
}

export interface WebsocketXpContestEvent extends BaseServerData {
  readonly type: 'xpContest';
  readonly parent_type: 'notifications';
  readonly data: WebsocketContestData & {
    readonly currency: string;
    readonly currencyCode: string;
    readonly maxbudget: string | number;
  };
}

export interface WebsocketTaskCreateEvent extends BaseServerData {
  readonly type: 'tasklistCreateV1';
  readonly parent_type: 'notifications';
  readonly data: TaskApi & {
    readonly linkUrl: string;
    readonly imgUrl: string;
  };
}

interface WebsocketTaskUpdateEvent extends BaseServerData {
  readonly type: 'tasklistUpdateV1';
  readonly parent_type: 'notifications';
  readonly data: TaskApi;
}

type WebsocketTaskEvent = WebsocketTaskCreateEvent | WebsocketTaskUpdateEvent;

// Events that are there but we're ignoring.
// TODO: T38861 for bidHidden. It's an AB test.
export interface WebsocketDeprecatedEvent extends BaseServerData {
  readonly type:
    | 'contactRequestSent'
    | 'contactRequestReceived'
    | 'bidHidden'
    | 'sitesBinCreated'
    | 'sitesHandOverMessageSent'
    | 'sitesListingStatusChanged'
    | 'sitesMessageSent'
    | 'sitesOfferCreated';
  readonly parent_type: 'notifications';
  readonly data: any;
}

// Events that are unknown/not defined yet
// Basically everything in event_notify.php that doesn't have a corresponding
// type in this file ends up here
export interface WebsocketUnknownEvent extends BaseServerData {
  readonly type:
    | 'reviewActivate'
    | 'reviewPending'
    | 'rejected'
    | 'projectStatusChange'
    | 'negotiatedProjectStatusChange'
    | 'uploadFile'
    | 'contestEntryCreated'
    | 'contestCreated'
    | 'contestUpsellFeaturedUpgrade'
    | 'counterOffer'
    | 'editAwardedBidRequest'
    | 'editAwardedBidAccepted'
    | 'editAwardedBidDeclined'
    | 'pmb'
    | 'pmb_public'
    | 'contest_pcb'
    | 'contestreviewposted'
    | 'completeContestReview'
    | 'completeReview'
    | 'reviewposted'
    | 'reviewpostednew'
    | 'updateMilestone'
    | 'releasePartMilestone'
    | 'invoiceWithdrawn'
    | 'invoicePaid'
    | 'invoicePartialPaid'
    | 'invoiceRequestChange'
    | 'invoiceAcceptedPartial'
    | 'directTransferDone'
    | 'newDispute'
    | 'sendDisputeMessage'
    | 'acceptDisputeOffer'
    | 'makeDisputeOffer'
    | 'escalateDispute'
    | 'aborted'
    | 'emailChange'
    | 'buyFreeMarketService'
    | 'freeMarketServiceExpired'
    | 'freeMarketServiceIsExpired'
    | 'sellFreeMarketService'
    | 'adminForceVarifyPhone'
    | 'yahooMailAlert'
    | 'adminWarningUserBidRestriction'
    | 'downgradeToFreeMembershipNoti'
    | 'upgradeToNonFreeMembership'
    | 'bidsRunningOut'
    | 'removeUserFromDirectory'
    | 'banUserFromBidding'
    | 'winningDesignReadyForReview'
    | 'contestComplete'
    | 'contestCompleteSellEntryUpsell'
    | 'prizeDispersedEmployer'
    | 'prizeDispersedFreelancer'
    | 'contestLockedEmployer'
    | 'contestLockedFreelancer'
    | 'welcome'
    | 'invoiceFeedback'
    | 'postDraftProject'
    | 'projectHireMeExpired'
    | 'customAdminNotification'
    | 'inviteFriends'
    | 'generic'
    | 'contestEntryRated'
    | 'sponsor_bid'
    | 'kyc'
    | 'suggestionForFreelancerAfterReceiveReview'
    | 'upsellPlusTrial'
    | 'upsellFeatureUpgrade'
    | 'upsellAssistedUpgrade'
    | 'tombstoneNotiEmployer'
    | 'tombstoneNotiFreelancer'
    | 'tombstoneNoti'
    | 'communityPromoNoti'
    | 'expertsPromoNoti'
    | 'localJobsActivation'
    | 'firstEarning'
    | 'serviceProjectPostedBuyer' // possible union with 'serviceProjectPostedSeller'?
    | 'serviceProjectEndedBuyer'
    | 'serviceProjectEndedSeller'
    | 'activateFreelancer'
    | 'promoteContest'
    | 'sitesBidCreated'
    | 'remindUseBids'
    | 'secondBidPlusTrialUpsell'
    | 'pendingFunds'
    | 'examPassedUpsell'
    | 'examRetakingDiscount'
    | 'userSideAction'
    | 'userSideActionFeedback'
    | 'supportQueueEvent' // special - see event_notify.php QueueNotifSender::NAME
    | 'projectSharedWithYou'
    | 'prehireProjectCreated'
    | 'collaboratorAddedViaChat'
    | 'collaboratorsUpdatedRefreshChat';
  readonly parent_type: 'notifications';
  readonly data: any;
}

type WebsocketNotificationEvent =
  | WebsocketAcceptedEvent
  | WebsocketArticleCommentEvent
  | WebsocketAwardBadgeEvent
  | WebsocketAwardCreditEvent
  | WebsocketAwardEvent
  | WebsocketAwardMilestoneReminderEvent
  | WebsocketAwardReminderEvent
  | WebsocketAwardXpEvent
  | WebsocketBalanceUpdateEvent
  | WebsocketBidEvent
  | WebsocketBidretractedEvent
  | WebsocketCancelMilestoneEvent
  | WebsocketCompletedEvent
  | WebsocketContestAwardedToEmployerEvent
  | WebsocketContestAwardedToFreelancerEvent
  | WebsocketContestEntryBoughtToEmployerEvent
  | WebsocketContestEntryBoughtToFreelancerEvent
  | WebsocketContestEntryEvent
  | WebsocketContestEvent
  | WebsocketContestExpiredEvent
  | WebsocketContestPCBNotificationEvent
  | WebsocketContestPCBNotificationFullViewEvent
  | WebsocketCreateMilestoneEvent
  | WebsocketDenyedEvent
  | WebsocketDeprecatedEvent
  | WebsocketDraftContestEvent
  | WebsocketFailingProjectEvent
  | WebsocketGiveGetChildPartialMilestoneRelease
  | WebsocketGiveGetChildReceivedSignupBonusEvent
  | WebsocketGiveGetChildSignUpEvent
  | WebsocketGiveGetParentBonusEvent
  | WebsocketHighLtvAnnualDiscountEvent
  | WebsocketHireMeEvent
  | WebsocketInviteToContestEvent
  | WebsocketInviteUserBidEvent
  | WebsocketInvoiceRequestedEvent
  | WebsocketLevelUpEvent
  | WebsocketLocalJobPostedEvent
  | WebsocketNotifyFollowerEvent
  | WebsocketProjectEvent
  | WebsocketQuickHireProjectEvent
  | WebsocketRecruiterProjectEvent
  | WebsocketReleaseMilestoneEvent
  | WebsocketRequestEndProjectEvent
  | WebsocketRequestMilestoneEvent
  | WebsocketRequestToReleaseEvent
  | WebsocketRevokedEvent
  | WebsocketServiceApprovedEvent
  | WebsocketServiceProjectEndingSellerEvent
  | WebsocketServiceProjectPostedSellerEvent
  | WebsocketServiceRejectedEvent
  | WebsocketServiceStatusChangedEvent
  | WebsocketSignUpFreeTrialUpsellEvent
  | WebsocketTaskEvent
  | WebsocketUnknownEvent
  | WebsocketUpsellCrowdsourceFindingEvent
  | WebsocketXpContestEvent;

// --- Online/offline events ---

export interface WebsocketOnlineOfflineSubscribeEvent {
  readonly type: 'statusget';
  readonly data: { readonly [userId: string]: OnlineOfflineUserStatusValueApi };
  readonly parent_type: 'onlineoffline';
}

export interface WebsocketOnlineOfflineStatusChangeEvent {
  readonly type: 'statuschange';
  readonly data: {
    readonly from: OnlineOfflineUserStatusValueApi;
    readonly timestamp: number;
    readonly to: OnlineOfflineUserStatusValueApi;
    readonly user: number;
  };
  readonly parent_type: 'onlineoffline';
}

type WebsocketOnlineOfflineEvent =
  | WebsocketOnlineOfflineSubscribeEvent
  | WebsocketOnlineOfflineStatusChangeEvent;

// --- Messaging events ---

export interface WebsocketGroupChatEvent extends BaseServerData {
  readonly type: 'groupchat';
  readonly parent_type: 'notifications';
  readonly data: {
    readonly action: 'add' | 'remove';
    readonly payload: {
      readonly members: ReadonlyArray<number>;
      readonly thread: number;
    };
  };
}

export interface WebsocketVideoEvent {
  readonly type:
    | 'video:request'
    | 'video:request:audio_only'
    | 'video:close'
    | 'video:accept'
    | 'video:reject';
  readonly parent_type: 'messages';
  readonly data: {
    readonly session_id: string;
    readonly thread_id: number;
    readonly user_id: number;
  };
}

export interface WebsocketNewMessageEvent extends BaseServerData {
  readonly type: 'private';
  readonly parent_type: 'messages';
  readonly data: {
    readonly attachments: ReadonlyArray<string>;
    readonly client_message_id?: string;
    readonly from_user: number;
    readonly id: number;
    readonly message: string;
    readonly message_source: SourceTypeApi;
    readonly notify_user: boolean;
    readonly parent_id: number;
    readonly rich_message?: RichMessageApi;
    readonly thread_details: {
      readonly context: {
        readonly id?: number;
        readonly type?: ContextTypeApi;
      };
      readonly creator_id: number;
      readonly members: ReadonlyArray<number>;
      readonly thread_type: ThreadTypeApi;
    };
    readonly thread_id: number;
    readonly time_created: number;
  };
}

export interface WebsocketTypingEvent {
  readonly type: 'typing';
  readonly parent_type: 'messages';
  readonly data: {
    readonly from_user: number;
    readonly thread: number;
  };
}

export interface WebsocketUserReadEvent extends BaseServerData {
  readonly type: 'user_read';
  readonly parent_type: 'messages';
  readonly data: {
    readonly id: string | number;
    readonly thread_ids: ReadonlyArray<number>;
    readonly user_id: number;
  };
}

export interface WebsocketUserAttachEvent extends BaseServerData {
  readonly type: 'attach';
  readonly parent_type: 'messages';
  readonly data: {
    readonly filenames: ReadonlyArray<string>; // deprecated, please use 'attachments' instead
    readonly from_user: number;
    readonly message_id: number;
    readonly thread_id: number;
    readonly time_created: number;
    readonly attachments: ReadonlyArray<{
      readonly attachment_id: number;
      readonly filename: string;
    }>;
  };
  readonly id: string;
}

export interface WebsocketThreadWritePrivacyUpdateEvent {
  readonly type: 'write_privacy_update';
  readonly parent_type: 'messages';
  readonly data: {
    readonly thread_ids: ReadonlyArray<number>;
    readonly write_privacy: PrivacyLevelApi;
  };
}

type WebsocketMessagingEvent =
  | WebsocketGroupChatEvent
  | WebsocketNewMessageEvent
  | WebsocketThreadWritePrivacyUpdateEvent
  | WebsocketTypingEvent
  | WebsocketUserReadEvent
  | WebsocketUserAttachEvent
  | WebsocketVideoEvent;

export type WebsocketEvent =
  | WebsocketNotificationEvent
  | WebsocketOnlineOfflineEvent
  | WebsocketMessagingEvent;

export function isOnlineOfflineEvent(
  event: WebsocketEvent,
): event is WebsocketOnlineOfflineEvent {
  return event.parent_type === 'onlineoffline';
}

export function isWebsocketMessage(
  event: WebsocketServerEvent,
): event is WebsocketMessage {
  return event.channel === 'user';
}

type BaseMessage = BaseServerData & {
  readonly channel: 'user';
  readonly body: {
    readonly aggregated: null;
    readonly display: boolean;
    readonly id: string;
    readonly is_important: boolean;
    readonly is_notification: boolean;
    readonly is_project: boolean;
    readonly message: string;
    readonly news_feed: boolean;
    readonly no_persist: boolean;
    readonly popup: boolean;
    readonly targets: ReadonlyArray<any>;
    readonly timestamp: number;
  };
};

export type WebsocketMessage = BaseMessage & {
  readonly body: WebsocketEvent;
};
